/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ModelEntityType } from '@upright/common/model/input/types';
import { bouTextFont } from '@upright/ui/common/generic/content/texts/bou-text/text-style';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { SvgDefs } from '@upright/ui/common/upright/core/impact-lens-profile/common/svg-defs';
import { LensErrorState } from '@upright/ui/common/upright/core/impact-lens-profile/error-state';
import { Frame, FrameDiv } from '@upright/ui/common/upright/core/impact-lens-profile/frame';
import { LensLoadingState } from '@upright/ui/common/upright/core/impact-lens-profile/loading-state';
import { AggregateResultArea } from '@upright/ui/common/upright/core/impact-lens-profile/main/aggregate-result-area';
import { DataAreas } from '@upright/ui/common/upright/core/impact-lens-profile/main/data-areas';
import { EmptyState } from '@upright/ui/common/upright/core/impact-lens-profile/main/empty-state';
import { InfoBoxes } from '@upright/ui/common/upright/core/impact-lens-profile/main/info-boxes';
import {
  ImpactLensProfileProps,
  LensData,
  LensOptions,
} from '@upright/ui/common/upright/core/impact-lens-profile/types';
import { NetImpactLensHint } from '@upright/ui/common/upright/core/impact-lenses/net-impact/lens-tip-popover';
import { ImpactLens, ImpactLensProfileLayer } from '@upright/ui/common/upright/core/impact-lenses/types';
import { getValueSetName, getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ImpactExplanationModal } from '../../model/output/scores/impact-explanation-modal';
import { useSelectionStateInLens } from '../impact-lenses/selection-hook';
import { baseLayout } from './common/layout/layout2';
import { carouselContext, CarouselContext } from './context';
import { AdditionalInfoArea } from './main/additional-info-area';
import { TotalSumsArea } from './main/total-sums-area';

interface ContentWithHistory {
  current: Content;
  previous: Content | undefined;
}

interface Content {
  data: LensData<any>;
  lensIndex: number;
}

const SVGLayer = styled.svg`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  font-family: ${bouTextFont};
  font-size: 11px;
`;

/*
const SVGDebugLayer = styled(SVGLayer)`
  pointer-events: none;
`;
*/

const DivLayer = styled.div`
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  font-family: ${bouTextFont};
  font-size: 11px;
  line-height: 1.1;
`;
const Root = styled.div`
  position: relative;
  overflow-x: clip;
`;

const Provider = carouselContext.Provider;

export function ImpactLensProfilePlain<
  A extends ImpactLensType,
  B extends LensData<A>,
  C extends LensOptions<A>,
  D extends ImpactLens<A, B, C>,
>(props: ImpactLensProfileProps<A, B, C, D>) {
  const [content, setContent] = useState<ContentWithHistory>({
    current: { data: {} as any, lensIndex: props.lensIndex },
    previous: undefined,
  });
  const valueSet = useSelector(getValueSetName);
  const version = useSelector(getVersionAndRelease);

  useEffect(() => {
    setContent(cntnt => {
      if (cntnt.current.data !== props.data || cntnt.current.lensIndex !== props.lensIndex) {
        return {
          current: {
            data: props.data,
            lensIndex: props.lensIndex,
          },
          previous: cntnt.current,
        };
      } else {
        return cntnt;
      }
    });
  }, [props.data, props.lensIndex]);

  const width = props.width || 1100;
  const lens = props.lens;

  const [selectionState, setSelectionState] = useSelectionStateInLens(props);

  // slightly awkward merge of passed and default options
  // simplify once sure there is no problem with types

  const optionsFromProps = props.options;
  const defaultOptions: LensOptions<A> = lens.defaultOptions
    ? lens.defaultOptions(props.data, selectionState, optionsFromProps)
    : { type: lens.type };
  const options: LensOptions<A> = { ...defaultOptions, ...optionsFromProps, ...{ valueSet, version } };

  const [modalImpactCode, setModalImpactCode] = useState<string | undefined>(undefined);

  const layout = baseLayout({
    width,
    boxMode: lens.boxMode && lens.boxMode(width, options),
    centerAreaWidth: lens.centerAreaWidth,
    boxesAreaWidth: lens.boxesAreaWidth,
    maxDataAreaWidth: lens.maxDataAreaWidth,
    hasAdditionalInfoArea: !!lens.AdditionalInfoArea,
    aggregateResultAreaHeight: lens.aggregateResultAreaHeight
      ? lens.aggregateResultAreaHeight(width, options)
      : undefined,
    hideTopDescriptionsArea: !!options.hideTopDescriptionArea,
    showTotalSums: lens.showTotalSumsArea ? lens.showTotalSumsArea(selectionState) : false,
  });
  // temporary debug helper
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  (window as any).clayout = layout;

  /*
  const debugContext = useDebugContext(layout);
  const showDebugInfo = useIsExpertMode();
  */

  const context: CarouselContext<A, B, C> = {
    layout,
    width,
    data: props.data,
    selectionState,
    setSelectionState,
    lens: props.lens,
    lensType: props.lens.type,
    lensIndex: content.current.lensIndex,
    previousLensIndex: content.previous ? content.previous.lensIndex : undefined,
    modalImpactCode,
    setModalImpactCode,
    negativeColor: BouColor.IMPACT_NEGATIVE_PURPLE,
    positiveColor: BouColor.IMPACT_POSITIVE_GREEN,
    desktopMode: true,
    options,
  };

  const fullArea = layout.fullArea();
  const style = {
    width: fullArea.width,
    height: fullArea.height,
  };

  const entityType = context.data.entity?.type;

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <Provider value={context}>
      <NetImpactLensHint />
      <Root style={style}>
        <SVGLayer style={style}>
          <SvgDefs />
          <Frame />
          <DataAreas />
          <InfoBoxes layer={ImpactLensProfileLayer.SVG} />
        </SVGLayer>
        <DivLayer style={style}>
          <FrameDiv />
          <InfoBoxes layer={ImpactLensProfileLayer.DIV} />
          <AdditionalInfoArea />
          <AggregateResultArea scoreUnit={options.scoreUnit} />
          <TotalSumsArea />
          <LensLoadingState />
          <LensErrorState />
          <EmptyState />
        </DivLayer>
        {/*

        <SVGDebugLayer style={style}>
          <DebugBoxes context={context} debugContext={debugContext} />
        </SVGDebugLayer>
  */}
        <ImpactExplanationModal
          isOpen={!!context.modalImpactCode}
          impactCode={context.modalImpactCode}
          type={entityType || ModelEntityType.COMPANY}
          onClose={() => {
            context.setModalImpactCode(undefined);
          }}
        />
      </Root>
      {/* showDebugInfo && <DebugControls debugContext={debugContext} context={context} /> */}
    </Provider>
  );
}
